import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useDebounce } from '../../../hooks/useDebounce'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { useContractMutations } from '../hooks/useContractMutations'
import {
  EMPTY_CONTRACT_FILTER, contractAffordances, groupContracts, isFiltered, toContractFilter,
  type ContractFilterState, type ContractGrouping,
} from '../lib/contracts'
import { describeCostControlError } from '../lib/errors'
import { fromTenderAllocate } from '../lib/integrations'
import { moneyLocale } from '../lib/money'
import type { CostContract, CostControlView } from '../types'
import ContractDialog from './ContractDialog'
import ContractsTable from './ContractsTable'
import ContractsTabSkeleton from './ContractsTabSkeleton'
import ContractsToolbar from './ContractsToolbar'
import FromTenderDialog from './FromTenderDialog'

interface Props { projectId: string }

const ContractsTab: React.FC<Props> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { history } = useRouter()
  const actions = useModuleActions('COST_CONTROL', projectId)
  const procurement = useModuleActions('PROCUREMENT', projectId)
  const af = contractAffordances(actions, procurement.canView, null)
  const mutations = useContractMutations(projectId)
  const [filter, setFilter] = useState<ContractFilterState>(EMPTY_CONTRACT_FILTER)
  const [search] = useDebounce(filter.search, 300)
  const [grouping, setGrouping] = useState<ContractGrouping>('levelOne')
  const [creating, setCreating] = useState(false)
  const [fromTender, setFromTender] = useState(false)

  const { data, loading, error } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: toContractFilter(filter, search) },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // The toolbar filter may hide the main contract, so "one exists" is asked unfiltered.
  const { data: mainData } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: { kind: 'MAIN' } },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    fetchPolicy: 'cache-first',
    context: { handlesErrors: true },
  })
  const currency = viewData?.costControl.settings.currency ?? 'CZK'
  const levelOne = useMemo(
    () => (viewData?.costControl.nodes ?? []).filter((n) => n.parentId === null).map((n) => ({ id: n.id, name: n.name })),
    [viewData],
  )
  const rows = data?.costContracts
  const groups = useMemo(() => (rows ? groupContracts(rows, grouping, levelOne, t) : []), [rows, grouping, levelOne, t])
  const open = (id: string, allocate?: string | null) =>
    history.push(`/project/${projectId}/cost-control/contracts/${id}${allocate ? `?allocate=${encodeURIComponent(allocate)}` : ''}`)

  if (!rows && loading) return <ContractsTabSkeleton />

  return (
    <div className="space-y-2">
      <ContractsToolbar
        count={rows?.length ?? 0}
        filter={filter}
        onFilterChange={setFilter}
        grouping={grouping}
        onGroupingChange={setGrouping}
        levelOne={levelOne}
        af={af}
        onCreate={() => setCreating(true)}
        onFromTender={() => setFromTender(true)}
      />
      {!rows && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
      {rows && rows.length === 0 && (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          {t(isFiltered(filter) ? 'CostControl_contracts_empty_filtered' : 'CostControl_contracts_empty')}
        </div>
      )}
      {rows && rows.length > 0 && <ContractsTable groups={groups} grouped={grouping !== 'none'} currency={currency} locale={locale} onOpen={(id) => open(id)} />}
      <ContractDialog
        open={creating}
        mode="create"
        allowMain={!!mainData && mainData.costContracts.length === 0}
        currency={currency}
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) open(res.data.createCostContract.id)
          return res.ok
        }}
      />
      <FromTenderDialog
        open={fromTender}
        projectId={projectId}
        currency={currency}
        locale={locale}
        onOpenChange={setFromTender}
        create={mutations.createFromTender}
        onCreated={(id, amount, itemCount, lineCount) => open(id, fromTenderAllocate(amount, itemCount, lineCount))}
      />
    </div>
  )
}
export default ContractsTab
